import React, { useState, useEffect } from 'react';
import axios from 'axios';
import type { Recommendation, TradeDetails } from '../types';
import { formatDate, formatDateShort } from '../utils/date';
import { formatCompanyName } from '../utils/format';

const ALPACA_ORDER_URL = 'https://app.alpaca.markets/dashboard/order';

interface TradeDetailModalProps {
  rec: Recommendation;
  onClose: () => void;
}

export function TradeDetailModal({ rec, onClose }: TradeDetailModalProps) {
  const [tradeDetails, setTradeDetails] = useState<TradeDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    axios
      .get<TradeDetails>(`/recommendations/${rec.id}/trade-details`)
      .then((res) => {
        if (!cancelled) setTradeDetails(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          if (err?.response?.status === 404) setTradeDetails({ trade_recommendation: null, trade_order: null, trade_events: [] });
          else setError(err?.response?.data?.detail || 'Failed to load trade details');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [rec.id]);

  const tr = tradeDetails?.trade_recommendation ?? null;
  const order = tradeDetails?.trade_order ?? null;
  const events = tradeDetails?.trade_events ?? [];
  const decision = tr?.ai_decision ?? rec.ai_decision ?? '';
  const decisionClass = decision ? `trade-decision-${decision.toLowerCase()}` : '';

  return (
    <div className="trade-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="trade-modal-viewbox"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="trade-modal-title"
      >
        <div className="trade-modal-header">
          <h2 id="trade-modal-title">{rec.ticker} — {formatCompanyName(rec.company_name)}</h2>
          <button type="button" className="trade-modal-close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="trade-modal-body">
          <div className="expanded-grid">
            <div className="info-section">
              <h4>Filing Details</h4>
              <dl>
                <div>
                  <dt>SEC Filing</dt>
                  <dd>
                    {rec.sec_url ? (
                      <a href={rec.sec_url} target="_blank" rel="noreferrer">
                        {rec.accession_number || 'View Filing'}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </a>
                    ) : (
                      <span className="text-muted">Not available</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Filing Date</dt>
                  <dd>{formatDate(rec.filing_date)}</dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatDate(rec.created_at)}</dd>
                </div>
              </dl>
            </div>

            {loading && (
              <div className="info-section">
                <p className="text-muted">Loading trade details…</p>
              </div>
            )}
            {error && (
              <div className="info-section">
                <p className="text-muted">{error}</p>
              </div>
            )}
            {!loading && !error && (tr || order || events.length > 0) && (
              <div className={`trade-details-block ${decisionClass}`}>
                <div className="trade-details-row">
                  <div className="trade-details-col trade-details-left">
                {tr && (
                  <div className="info-section">
                    <h4>Trade Recommendation (AI)</h4>
                    <dl>
                      <div>
                        <dt>Decision</dt>
                        <dd>{tr.ai_decision ?? '—'}</dd>
                      </div>
                      <div>
                        <dt>Position Size</dt>
                        <dd>{tr.ai_position_size != null ? `${(tr.ai_position_size * 100).toFixed(1)}%` : '—'}</dd>
                      </div>
                      <div>
                        <dt>AI Stop Loss</dt>
                        <dd>{tr.ai_stop_loss != null ? `$${tr.ai_stop_loss.toFixed(2)}` : '—'}</dd>
                      </div>
                      <div>
                        <dt>AI Target Price</dt>
                        <dd>{tr.ai_target_price != null ? `$${tr.ai_target_price.toFixed(2)}` : '—'}</dd>
                      </div>
                      {tr.ai_confidence != null && (
                        <div>
                          <dt>Confidence</dt>
                          <dd>{(tr.ai_confidence * 100).toFixed(1)}%</dd>
                        </div>
                      )}
                      {tr.created_at && (
                        <div>
                          <dt>Created</dt>
                          <dd>{formatDate(tr.created_at)}</dd>
                        </div>
                      )}
                    </dl>
                    {tr.ai_reasoning && (
                      <div className="trade-reasoning-full">
                        <p>{tr.ai_reasoning}</p>
                      </div>
                    )}
                  </div>
                )}
                  </div>
                  <div className="trade-details-col trade-details-right">
                {order && (
                  <div className="info-section">
                    <h4>Trade Execution</h4>
                    <dl>
                      <div>
                        <dt>Side / Quantity</dt>
                        <dd>{order.side ?? '—'} / {order.qty ?? '—'}</dd>
                      </div>
                      <div>
                        <dt>Limit Price</dt>
                        <dd>{order.limit_price != null ? `$${order.limit_price.toFixed(2)}` : '—'}</dd>
                      </div>
                      <div>
                        <dt>Stop Loss</dt>
                        <dd>{order.stop_loss_price != null ? `$${order.stop_loss_price.toFixed(2)}` : '—'}</dd>
                      </div>
                      <div>
                        <dt>Take Profit</dt>
                        <dd>{order.take_profit_price != null ? `$${order.take_profit_price.toFixed(2)}` : '—'}</dd>
                      </div>
                      {order.alpaca_order_id && (
                        <div>
                          <dt>Alpaca Order</dt>
                          <dd>
                            <a
                              href={`${ALPACA_ORDER_URL}/${order.alpaca_order_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="alpaca-order-link"
                            >
                              {order.alpaca_order_id}
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                            </a>
                          </dd>
                        </div>
                      )}
                      {order.submitted_at && (
                        <div>
                          <dt>Submitted</dt>
                          <dd>{formatDate(order.submitted_at)}</dd>
                        </div>
                      )}
                      {order.error_message && (
                        <div>
                          <dt>Error</dt>
                          <dd className="text-muted">{order.error_message}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
                {events.length > 0 && (
                  <div className="info-section">
                    <h4>Trade Events ({events.length})</h4>
                    <ul className="trade-events-list">
                      {events.map((ev) => (
                        <li key={ev.id}>
                          <span className="event-type">{ev.event_type}</span>
                          {ev.message && <span className="event-message">{ev.message}</span>}
                          <span className="event-time">{formatDateShort(ev.created_at)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
